import { useCallback, useMemo, useState } from 'react'
import AgentsTab from '../components/management/AgentsTab'
import CouponsTab from '../components/management/CouponsTab'
import CustomersTab from '../components/management/CustomersTab'
import PartnersTab from '../components/management/PartnersTab'
import { useSocket } from '../hooks/useSocket'

// NOTE: service catalog management lives on the dedicated /service-pricing page
// (ServicePricingPage), which is fully wired (service + item active toggles,
// pricing, add/delete). The old "Service Catalog" tab here was a redundant,
// non-persisting duplicate and has been removed to avoid confusion.
type ManagementTab = 'agents' | 'partners' | 'customers' | 'coupons'

const tabs: Array<{ key: ManagementTab; label: string }> = [
  { key: 'agents', label: 'Agents' },
  { key: 'partners', label: 'Partners' },
  { key: 'customers', label: 'Customers' },
  { key: 'coupons', label: 'Coupons' },
]

const getInitialTab = (): ManagementTab => {
  const searchParams = new URLSearchParams(window.location.search)
  const tab = searchParams.get('tab')

  return tabs.some((item) => item.key === tab) ? (tab as ManagementTab) : 'agents'
}

export default function ManagementPage() {
  const [activeTab, setActiveTab] = useState<ManagementTab>(getInitialTab)
  const [agentsBadgeCount, setAgentsBadgeCount] = useState(0)
  const [agentsRefreshToken, setAgentsRefreshToken] = useState(0)

  const bumpAgentsBadge = useCallback(() => {
    setAgentsBadgeCount((prev) => Math.min(prev + 1, 99))
    setAgentsRefreshToken((prev) => prev + 1)
  }, [])

  useSocket({
    'agent:registered': bumpAgentsBadge,
    'agent:kyc_doc_uploaded': bumpAgentsBadge,
    'agent:kyc_doc_reviewed': bumpAgentsBadge,
    'agent:kyc_status_changed': bumpAgentsBadge,
  })

  const tabLabels = useMemo(() => {
    return tabs.map((tab) => {
      if (tab.key !== 'agents' || agentsBadgeCount === 0) return tab
      return { ...tab, label: `Agents (${agentsBadgeCount})` }
    })
  }, [agentsBadgeCount])

  return (
    <div>
      <div style={{ margin: '24px 32px 0' }}>
        <div
          style={{
            display: 'inline-flex',
            background: '#F3EFE6',
            borderRadius: '16px',
            padding: '4px',
            gap: '4px',
          }}
        >
          {tabLabels.map((tab) => {
            const isActive = activeTab === tab.key

            return (
              <button
                key={tab.key}
                type="button"
                className="font-body"
                onClick={() => {
                  setActiveTab(tab.key)
                  if (tab.key === 'agents') setAgentsBadgeCount(0)
                }}
                style={{
                  padding: '8px 20px',
                  borderRadius: '12px',
                  border: 'none',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  fontSize: '14px',
                  background: isActive ? '#0F2E2A' : 'transparent',
                  color: isActive ? '#FFFFFF' : '#4A5568',
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {activeTab === 'agents' ? (
        <AgentsTab refreshToken={agentsRefreshToken} />
      ) : null}
      {activeTab === 'coupons' ? <CouponsTab /> : null}
      {activeTab === 'partners' ? <PartnersTab /> : null}
      {activeTab === 'customers' ? <CustomersTab /> : null}
    </div>
  )
}
